import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { STAMP_DEFINITIONS, STAMP_DEFINITIONS_MIRRORED_ON } from '../data/challengeStamps';
import { loadStampHolders, type StampHolder, type StampHoldersResult } from '../lib/stampHolders';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { ExportButton } from './ExportButton';

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * BROWSE BY STAMP — "pick a stamp, see who holds it"
 *
 * The inverse of the app's stamps popup, which asks "which stamps does this person have". Lives on
 * the Users page, which is already `access.isAdmin`-gated in `App.tsx`, so no new gate is needed and
 * no new route was added.
 *
 * COLLAPSED BY DEFAULT, AND THAT IS THE FETCH BOUNDARY: the whole rating corpus is read to compute
 * this, and the Users page's own paginated table must not pay for it. Nothing loads until Show is
 * pressed; after that the result is held for the life of the page and Refresh re-reads it.
 *
 * SCOPE — this counts what an ADMIN can see, which is wider than the app's public slice. The
 * difference is tabulated in `src/lib/stampHolders.ts`; the caption under the tiles states it on
 * screen so nobody compares these numbers to the app's and concludes something is broken.
 * ──────────────────────────────────────────────────────────────────────────────────────────────── */

function pct(n: number, of: number): string {
  if (of <= 0) return '—';
  const p = (n / of) * 100;
  return p >= 10 || p === 0 ? `${Math.round(p)}%` : `${p.toFixed(1)}%`;
}

export function StampHoldersPanel() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<StampHoldersResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>(
    STAMP_DEFINITIONS[STAMP_DEFINITIONS.length - 1]?.id ?? '',
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await loadStampHolders());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load stamp holders');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Fetch on first expand only — see the note above. Refresh is the explicit re-read. */
  useEffect(() => {
    if (open && !result && !loading && !error) void load();
  }, [open, result, loading, error, load]);

  const selected = useMemo(
    () => STAMP_DEFINITIONS.find((d) => d.id === selectedId) ?? null,
    [selectedId],
  );
  const holders: StampHolder[] = result?.holdersByStampId[selectedId] ?? [];
  const raters = result?.ratersEvaluated ?? 0;

  return (
    <section className="mb-8 rounded-xl border border-border bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
        <div className="min-w-0">
          <h2 className="admin-label-primary text-base">Stamps</h2>
          <div className="mt-0.5 text-xs text-mutedDim">
            Pick a stamp to see everyone who holds it.
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {open && result ? (
            <ExportButton
              rows={holders}
              columns={[
                { header: 'stamp', get: () => selected?.title ?? '' },
                { header: 'username', get: (r: StampHolder) => r.username ?? '' },
                { header: 'full_name', get: (r: StampHolder) => r.fullName ?? '' },
                { header: 'venues_rated', get: (r: StampHolder) => r.venuesRated },
                { header: 'cities_rated', get: (r: StampHolder) => r.citiesRated },
                { header: 'bars_rated', get: (r: StampHolder) => r.barsRated },
                { header: 'clubs_rated', get: (r: StampHolder) => r.clubsRated },
                { header: 'city', get: (r: StampHolder) => r.city ?? '' },
                { header: 'university', get: (r: StampHolder) => r.university ?? '' },
                { header: 'is_private', get: (r: StampHolder) => Boolean(r.isPrivate) },
                { header: 'created_at', get: (r: StampHolder) => r.createdAt ?? '' },
              ]}
              filename={`stamp-${selected?.id ?? 'holders'}`}
            />
          ) : null}
          {/*
            Refresh stays reachable while `result` is null, because a failed first load leaves it
            null forever — the auto-fetch effect below deliberately does not retry after an error,
            so this button is the only way back.
          */}
          {open ? (
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="admin-btn admin-btn-secondary disabled:opacity-40"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="admin-btn admin-btn-secondary"
            aria-expanded={open}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-border px-5 py-5">
          {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}

          {loading && !result ? (
            <SkeletonRows rows={6} className="py-2" />
          ) : result ? (
            <>
              <div className="flex flex-wrap gap-3" role="group" aria-label="Pick a stamp">
                {STAMP_DEFINITIONS.map((def) => {
                  const count = result.holdersByStampId[def.id]?.length ?? 0;
                  const active = def.id === selectedId;
                  return (
                    <button
                      key={def.id}
                      type="button"
                      onClick={() => setSelectedId(def.id)}
                      aria-pressed={active}
                      title={def.requirement}
                      className={`w-[148px] rounded-lg border px-3 py-3 text-left transition-colors ${
                        active
                          ? 'border-accent bg-accentSoft'
                          : 'border-border bg-white/[0.02] hover:bg-white/[0.05]'
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-lg leading-none" aria-hidden>
                          {def.emoji}
                        </span>
                        <span className="admin-metric">{count.toLocaleString()}</span>
                      </div>
                      <div className="admin-label-primary mt-2 truncate">{def.title}</div>
                      <div className="mt-0.5 text-[11px] leading-snug text-mutedDim">
                        {pct(count, raters)} of raters
                      </div>
                    </button>
                  );
                })}
              </div>

              {/*
                Scope caption. It is on screen rather than only in the source because the obvious
                next move is to compare these counts against the app, and they will not match —
                see the table in `src/lib/stampHolders.ts`.
              */}
              <p className="mt-4 max-w-3xl text-[11px] leading-relaxed text-mutedDim">
                Computed over{' '}
                <span className="tabular-nums text-muted">
                  {result.ratingsEvaluated.toLocaleString()}
                </span>{' '}
                live ratings from{' '}
                <span className="tabular-nums text-muted">{raters.toLocaleString()}</span> raters —
                every non-retired, non-deleted rating this admin account can read, private accounts
                included. That is WIDER than the app's own stamp counts, which see only publicly
                visible ratings. Criteria mirrored from the app's <code>CHALLENGE_BADGES</code> on{' '}
                {STAMP_DEFINITIONS_MIRRORED_ON}.
              </p>

              <div className="mt-6 mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="admin-label-primary text-sm">
                  {selected ? `${selected.emoji} ${selected.title}` : 'Holders'}
                </h3>
                <span className="text-[11px] text-mutedDim">
                  {selected?.requirement}
                  {' · '}
                  <span className="tabular-nums">{holders.length.toLocaleString()}</span>{' '}
                  {holders.length === 1 ? 'holder' : 'holders'}
                </span>
              </div>

              <div className="rounded-xl border border-border bg-surface overflow-x-auto">
                <table className="w-full border-collapse text-sm min-w-[820px]">
                  <thead>
                    <tr className="admin-table-head">
                      <th
                        scope="col"
                        className="sticky top-0 z-10 w-10 border-b border-border/80 bg-surface px-3 py-3.5 text-right"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        className="sticky top-0 z-10 w-12 border-b border-border/80 bg-surface px-4 py-3.5"
                        aria-label="Avatar"
                      />
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">
                        Username
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                        Venues
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                        Cities
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                        Bars
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                        Clubs
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">
                        Privacy
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">
                        City
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">
                        University
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {holders.map((h, i) => {
                      const isPrivate = Boolean(h.isPrivate);
                      return (
                        <tr
                          key={h.id}
                          className={`border-b border-border/50 transition-colors hover:bg-white/[0.03] ${
                            i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'
                          }`}
                        >
                          <td className="px-3 py-4 text-right font-mono text-2xs tabular-nums text-mutedDim">
                            {(i + 1).toLocaleString()}
                          </td>
                          <td className="px-4 py-4">
                            <div className="h-9 w-9 overflow-hidden rounded-full bg-border flex items-center justify-center text-xs text-muted">
                              {h.avatarUrl ? (
                                <img
                                  src={h.avatarUrl}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                (h.username ?? '?').slice(0, 1).toUpperCase()
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="admin-label-primary">{h.username ?? '—'}</div>
                            {h.fullName?.trim() ? (
                              <div className="mt-0.5 max-w-[160px] truncate text-xs text-mutedDim">
                                {h.fullName}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-4 text-right admin-metric tabular-nums">{h.venuesRated}</td>
                          <td className="px-4 py-4 text-right admin-metric tabular-nums">{h.citiesRated}</td>
                          <td className="px-4 py-4 text-right admin-metric tabular-nums">{h.barsRated}</td>
                          <td className="px-4 py-4 text-right admin-metric tabular-nums">{h.clubsRated}</td>
                          <td className="px-4 py-4">
                            <span
                              className={`admin-status ${
                                isPrivate ? 'admin-status-warning' : 'admin-status-success'
                              }`}
                            >
                              {isPrivate ? 'Private' : 'Public'}
                            </span>
                          </td>
                          <td className="px-4 py-4 max-w-[160px] truncate text-xs text-muted">
                            {h.city?.trim() || '—'}
                          </td>
                          <td className="px-4 py-4 max-w-[160px] truncate text-xs text-muted">
                            {h.university?.trim() || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {holders.length === 0 ? (
                  <div className="px-4 py-8">
                    <EmptyState
                      title="Nobody has this stamp yet"
                      hint={selected ? `It unlocks at: ${selected.requirement}` : undefined}
                    />
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
